import useSWR from "swr";
import { useState } from "react";
import { Loader2, LogOut, LayoutDashboard } from "lucide-react";
import { Toaster, toast } from "sonner";
import { adminApi } from "@/lib/api";
import { LOGO_URL } from "@/lib/site";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function LoginForm({ onOk }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await adminApi.post("/auth/login", { email, password });
      if (user?.access_token) {
        localStorage.setItem("orvella_admin_token", user.access_token);
      }
      toast.success("Welcome back");
      onOk(user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ivory px-5">
      <Toaster position="top-center" />
      <form onSubmit={submit} className="card w-full max-w-sm p-8" data-testid="admin-login-form">
        <img src={LOGO_URL} alt="Orvella Overseas logo" className="mx-auto h-14 w-auto object-contain" />
        <h1 className="mt-6 text-center font-serif text-2xl font-semibold text-navy">Admin Dashboard</h1>
        <p className="mt-1 text-center text-xs text-mist">Orvella Overseas — restricted access</p>
        <div className="mt-6 space-y-4">
          <div>
            <label className="flabel">Email</label>
            <input className="field" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" autoComplete="username" />
          </div>
          <div>
            <label className="flabel">Password</label>
            <input className="field" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" autoComplete="current-password" />
          </div>
        </div>
        {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-gold mt-6 w-full" data-testid="admin-login-submit">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <LayoutDashboard size={16} />}
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const { data: user, error, isLoading, mutate } = useSWR("/auth/me", (url) => adminApi.get(url), { shouldRetryOnError: false });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <Loader2 size={28} className="animate-spin text-gold-dark" />
      </div>
    );
  }
  if (error || !user) return <LoginForm onOk={(u) => mutate(u, { revalidate: false })} />;

  const logout = async () => {
    localStorage.removeItem("orvella_admin_token");
    await adminApi.post("/auth/logout", {});
    mutate(null, { revalidate: false });
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Toaster position="top-center" />
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-white px-5 py-3">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Orvella Overseas logo" className="h-9 w-auto object-contain" />
          <span className="hidden text-xs font-bold uppercase tracking-wider text-mist sm:inline">Admin Dashboard</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-mist">{user.email}</span>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-navy hover:border-gold hover:text-gold-dark">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>
      <AdminDashboard />
    </div>
  );
}
